import {
  getV1CompaniesCompanyIdJobsJobIdLoads,
  getV1CompaniesCompanyIdJobsJobIdTickets,
  getV1CompaniesCompanyIdOrdersOrderIdJobsInvoicing,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback, useContext, useMemo, useRef, useState } from 'react';

import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { Job, JobInvoicing } from '~hooks/useJob/models';
import { Load } from '~hooks/useLoad/models';
import { Ticket } from '~hooks/useTickets/models';
import FilesQueryContext from '~pages/Approvals/Files/FilesQueryContext';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import JobsQueryContext from '../../OrdersList/ExpandedBlock/JobsQueryContext';
import LoadsQueryContext from './LoadsQueryContext';

type CrossOrderState = {
  orderId: string;
  job: Job;
  jobs: Job[];
  loads: Load[];
  tickets: Ticket[];
};

// `undefined` means the drawer is closed; the sentinel means it is open on a job with no
// loads, which still needs working prev/next.
export const LOADLESS_JOB = 'loadless-job';
type OpenLoadId = string | typeof LOADLESS_JOB | undefined;

type UseCrossOrderNavigationOptions = {
  onNavigate: (load?: Load, ticket?: Ticket) => void;
};

export default function useCrossOrderNavigation({
  onNavigate,
}: UseCrossOrderNavigationOptions) {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  const filesQuery = useContext(FilesQueryContext);
  const jobsQuery = useContext(JobsQueryContext);
  const loadsQuery = useContext(LoadsQueryContext);

  const [crossOrderState, setCrossOrderState] = useState<CrossOrderState | null>(null);
  const [isFetching, setIsFetching] = useState(false);

  // Use ref to avoid stale closures in async callbacks
  const crossOrderStateRef = useRef(crossOrderState);
  crossOrderStateRef.current = crossOrderState;

  const fetchJobsForOrder = useCallback(
    async (orderId: string) => {
      const response = await getV1CompaniesCompanyIdOrdersOrderIdJobsInvoicing({
        path: { 'company-id': companyId, 'order-id': orderId },
        query: { 'page[limit]': MAX_API_PAGE_SIZE },
      });

      return response.data.data.map((j) => JobInvoicing.parse(j));
    },
    [companyId],
  );

  const findAdjacentJob = useCallback(
    async (
      currentJobId: string,
      direction: 'next' | 'previous',
      overrideJobs?: Job[],
      overrideOrderId?: string,
    ): Promise<{
      job: Job;
      jobs: Job[];
      orderId: string;
    } | null> => {
      const currentState = crossOrderStateRef.current;

      const currentOrderId =
        overrideOrderId ?? currentState?.orderId ?? jobsQuery.order.id;
      const currentJobs =
        overrideJobs ?? currentState?.jobs ?? (await fetchJobsForOrder(currentOrderId));

      const jobIndex = currentJobs.findIndex((j) => j.id === currentJobId);
      if (jobIndex < 0) return null;

      // Try next/previous job in the same order
      const adjacentIndex = direction === 'next' ? jobIndex + 1 : jobIndex - 1;
      if (adjacentIndex >= 0 && adjacentIndex < currentJobs.length) {
        return {
          job: currentJobs[adjacentIndex],
          jobs: currentJobs,
          orderId: currentOrderId,
        };
      }

      // Exactly one step. Walking past orders that look unapprovable turned a single click
      // into a burst of sequential requests and timed the server out (HORIZON-API-2VY).
      let orders = filesQuery.data;
      const orderIndex = orders.findIndex((o) => o.id === currentOrderId);
      if (orderIndex < 0) return null;

      const step = direction === 'next' ? 1 : -1;
      const adjacentOrderIndex = orderIndex + step;
      if (adjacentOrderIndex < 0) return null;

      // There is no "fetch previous page", so backwards stops at the first loaded order.
      if (adjacentOrderIndex >= orders.length) {
        if (direction !== 'next' || !filesQuery.hasMore) return null;

        orders = (await filesQuery.fetchMore()).data;
      }

      const adjacentOrder = orders[adjacentOrderIndex];
      if (!adjacentOrder) return null;

      const jobs = await fetchJobsForOrder(adjacentOrder.id);
      if (jobs.length === 0) return null;

      const targetJob = direction === 'next' ? jobs[0] : jobs[jobs.length - 1];

      return { job: targetJob, jobs, orderId: adjacentOrder.id };
    },
    [
      fetchJobsForOrder,
      filesQuery.data,
      filesQuery.fetchMore,
      filesQuery.hasMore,
      jobsQuery.order.id,
    ],
  );

  const navigateToJob = useCallback(
    async (
      job: Job,
      orderId: string,
      jobs: Job[],
      direction: 'next' | 'previous',
    ): Promise<void> => {
      const [loadsResponse, ticketsResponse] = await Promise.all([
        getV1CompaniesCompanyIdJobsJobIdLoads({
          path: { 'company-id': companyId, 'job-id': job.id },
          query: { 'page[limit]': MAX_API_PAGE_SIZE },
        }),
        getV1CompaniesCompanyIdJobsJobIdTickets({
          path: { 'company-id': companyId, 'job-id': job.id },
          query: { 'page[limit]': MAX_API_PAGE_SIZE },
        }),
      ]);

      const loads = loadsResponse.data.data.map((l) => Load.parse(l));
      const tickets = ticketsResponse.data.data.map((t) => Ticket.parse(t));

      const targetLoad =
        loads.length === 0
          ? undefined
          : direction === 'next'
            ? loads[0]
            : loads[loads.length - 1];
      const targetTicket = targetLoad
        ? tickets.find((t) => t.loadId === targetLoad.id)
        : undefined;

      setCrossOrderState({ orderId, job, jobs, loads, tickets });
      onNavigate(targetLoad, targetTicket);
    },
    [companyId, onNavigate],
  );

  const isAtEndOfQueue = useMemo(() => {
    const currentOrderId = crossOrderState?.orderId ?? jobsQuery.order.id;
    const orderIndex = filesQuery.data.findIndex((o) => o.id === currentOrderId);

    if (orderIndex < 0) return false;

    return orderIndex >= filesQuery.data.length - 1 && !filesQuery.hasMore;
  }, [crossOrderState?.orderId, filesQuery.data, filesQuery.hasMore, jobsQuery.order.id]);

  const isAtStartOfQueue = useMemo(() => {
    const currentOrderId = crossOrderState?.orderId ?? jobsQuery.order.id;

    return filesQuery.data.findIndex((o) => o.id === currentOrderId) <= 0;
  }, [crossOrderState?.orderId, filesQuery.data, jobsQuery.order.id]);

  const getCrossOrderNext = useCallback(
    (openLoadId: OpenLoadId, localLoads: Load[]): (() => Promise<void>) | null => {
      if (!openLoadId) return null;

      const currentState = crossOrderStateRef.current;
      const loads = currentState?.loads ?? localLoads;

      if (openLoadId !== LOADLESS_JOB) {
        const idx = loads.findIndex((l) => l.id === openLoadId);
        // Not at the boundary — local navigation handles it
        if (idx === -1 || idx < loads.length - 1) return null;
      }

      const currentJobId = currentState?.job.id ?? loadsQuery.job.id;

      // Withholding the handler is what disables the arrow, so it can explain itself
      // instead of staying live and doing nothing on click.
      const knownJobs = currentState?.jobs ?? jobsQuery.data ?? [];
      const jobIndex = knownJobs.findIndex((j) => j.id === currentJobId);
      const isLastKnownJob = jobIndex >= 0 && jobIndex === knownJobs.length - 1;
      if (isLastKnownJob && isAtEndOfQueue) return null;

      return async () => {
        setIsFetching(true);
        try {
          const result = await findAdjacentJob(currentJobId, 'next');
          if (result) {
            await navigateToJob(result.job, result.orderId, result.jobs, 'next');
          }
        } catch (error) {
          connection.handleRequestError(
            error,
            t('error_messages.jobs.failed_to_navigate_to_adjacent_load'),
          );
        } finally {
          setIsFetching(false);
        }
      };
    },
    [findAdjacentJob, isAtEndOfQueue, jobsQuery.data, loadsQuery.job.id, navigateToJob],
  );

  const getCrossOrderPrevious = useCallback(
    (openLoadId: OpenLoadId, localLoads: Load[]): (() => Promise<void>) | null => {
      if (!openLoadId) return null;

      const currentState = crossOrderStateRef.current;
      const loads = currentState?.loads ?? localLoads;

      if (openLoadId !== LOADLESS_JOB) {
        const idx = loads.findIndex((l) => l.id === openLoadId);
        // Not at the boundary — local navigation handles it
        if (idx !== 0) return null;
      }

      const currentJobId = currentState?.job.id ?? loadsQuery.job.id;

      const knownJobs = currentState?.jobs ?? jobsQuery.data ?? [];
      const jobIndex = knownJobs.findIndex((j) => j.id === currentJobId);
      if (jobIndex === 0 && isAtStartOfQueue) return null;

      return async () => {
        setIsFetching(true);
        try {
          const result = await findAdjacentJob(currentJobId, 'previous');
          if (result) {
            await navigateToJob(result.job, result.orderId, result.jobs, 'previous');
          }
        } catch (error) {
          connection.handleRequestError(
            error,
            t('error_messages.jobs.failed_to_navigate_to_adjacent_load'),
          );
        } finally {
          setIsFetching(false);
        }
      };
    },
    [findAdjacentJob, isAtStartOfQueue, jobsQuery.data, loadsQuery.job.id, navigateToJob],
  );

  const refreshCrossOrderLoads = useCallback(async () => {
    const state = crossOrderStateRef.current;
    if (!state) return;

    const [loadsResponse, ticketsResponse] = await Promise.all([
      getV1CompaniesCompanyIdJobsJobIdLoads({
        path: { 'company-id': companyId, 'job-id': state.job.id },
        query: { 'page[limit]': MAX_API_PAGE_SIZE },
      }),
      getV1CompaniesCompanyIdJobsJobIdTickets({
        path: { 'company-id': companyId, 'job-id': state.job.id },
        query: { 'page[limit]': MAX_API_PAGE_SIZE },
      }),
    ]);

    const loads = loadsResponse.data.data.map((l) => Load.parse(l));
    const tickets = ticketsResponse.data.data.map((t) => Ticket.parse(t));

    setCrossOrderState((prev) => (prev ? { ...prev, loads, tickets } : null));
  }, [companyId]);

  const updateCrossOrderTicket = useCallback((ticket: Ticket, isNew?: boolean) => {
    setCrossOrderState((prev) => {
      if (!prev) return null;

      if (isNew) {
        return { ...prev, tickets: [...prev.tickets, ticket] };
      }

      return {
        ...prev,
        tickets: prev.tickets.map((t) => (t.id === ticket.id ? ticket : t)),
      };
    });
  }, []);

  const resetCrossOrderState = useCallback(() => {
    setCrossOrderState(null);
  }, []);

  return {
    crossOrderState,
    isAtEndOfQueue,
    isAtStartOfQueue,
    isFetching,
    getCrossOrderNext,
    getCrossOrderPrevious,
    refreshCrossOrderLoads,
    updateCrossOrderTicket,
    resetCrossOrderState,
  };
}
